// src/hooks/usePortfolioState.js
import { useState, useEffect } from 'react';
import { 
  getPortfolioTargets, 
  savePortfolioTargets, 
  updateCategory,
  deleteCategory 
} from '../services/firebase/portfolioService';

export function usePortfolioState(userEmail, selectedPortfolioId = 'default') {
  const [portfolioData, setPortfolioData] = useState({});
  const [loading, setLoading] = useState(true);
  const [modalState, setModalState] = useState({
    showAddAsset: false,
    showAddCategory: false,
    selectedParentPath: null
  });

  useEffect(() => {
    // Portföy değiştiğinde verileri sıfırla
    setPortfolioData({});
    
    const loadData = async () => {
      if (!userEmail || !selectedPortfolioId) {
        console.warn('No email or portfolio ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('Loading portfolio data for:', userEmail);
        const data = await getPortfolioTargets(userEmail, selectedPortfolioId);
        setPortfolioData(data);
      } catch (error) {
        console.error('Error loading portfolio data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userEmail, selectedPortfolioId]);

  const handleAddAsset = (parentPath) => {
    setModalState({
      ...modalState,
      showAddAsset: true,
      selectedParentPath: parentPath
    });
  };

  const handleAssetSubmit = async (assetData) => {
    try {
      const updatedData = { ...portfolioData };
      const pathParts = modalState.selectedParentPath ? modalState.selectedParentPath.split('.') : [];
      const mainCategory = pathParts[0];
      const isSubCategory = pathParts.includes('subCategories');
      const subCategoryName = isSubCategory ? pathParts[2] : null;

      const assetEntry = {
        symbol: assetData.symbol,
        allocation: parseFloat(assetData.allocation) || 0,
        ...(assetData.description && { description: assetData.description })
      };

      if (isSubCategory && subCategoryName) {
        if (!updatedData[mainCategory]) {
          updatedData[mainCategory] = {
            name: mainCategory,
            allocation: 0,
            subCategories: {}
          };
        }

        if (!updatedData[mainCategory].subCategories) {
          updatedData[mainCategory].subCategories = {};
        }

        if (!updatedData[mainCategory].subCategories[subCategoryName]) {
          updatedData[mainCategory].subCategories[subCategoryName] = {
            name: subCategoryName,
            allocation: 0,
            assets: {}
          };
        }

        if (!updatedData[mainCategory].subCategories[subCategoryName].assets) {
          updatedData[mainCategory].subCategories[subCategoryName].assets = {};
        }

        updatedData[mainCategory].subCategories[subCategoryName].assets[assetData.symbol] = assetEntry;
        await updateCategory(userEmail, mainCategory, updatedData[mainCategory], selectedPortfolioId);
      } else {
        const category = mainCategory || assetData.type;
        if (!updatedData[category]) {
          updatedData[category] = {
            name: category,
            allocation: 0,
            assets: {}
          };
        }

        if (!updatedData[category].assets) {
          updatedData[category].assets = {};
        }

        updatedData[category].assets[assetData.symbol] = assetEntry;
        await updateCategory(userEmail, category, updatedData[category], selectedPortfolioId);
      }

      setPortfolioData(updatedData);
      console.log('Asset added successfully:', assetEntry);
    } catch (error) {
      console.error('Error adding asset:', error);
      throw error;
    }

    setModalState({
      ...modalState,
      showAddAsset: false,
      selectedParentPath: null
    });
  };

  const handleAddCategory = (parentPath) => {
    setModalState({
      ...modalState,
      showAddCategory: true,
      selectedParentPath: parentPath
    });
  };

  const handleCategorySubmit = async (categoryData) => {
    try {
      const updatedData = { ...portfolioData };
      
      if (modalState.selectedParentPath) {
        const mainCategory = modalState.selectedParentPath.split('.')[0];
        if (!updatedData[mainCategory]) {
          updatedData[mainCategory] = {
            name: mainCategory,
            allocation: 0,
            subCategories: {},
            assets: {}
          };
        }

        if (!updatedData[mainCategory].subCategories) {
          updatedData[mainCategory].subCategories = {};
        }

        updatedData[mainCategory].subCategories[categoryData.name] = {
          name: categoryData.name,
          allocation: categoryData.allocation || 0,
          assets: {}
        };

        await updateCategory(userEmail, mainCategory, updatedData[mainCategory], selectedPortfolioId);
      } else {
        updatedData[categoryData.name] = {
          name: categoryData.name,
          allocation: categoryData.allocation || 0,
          subCategories: {},
          assets: {}
        };

        await savePortfolioTargets(userEmail, updatedData, selectedPortfolioId);
      }
      
      setPortfolioData(updatedData);
    } catch (error) {
      console.error('Error adding category:', error);
    }
    
    setModalState({
      ...modalState,
      showAddCategory: false,
      selectedParentPath: null
    });
  };



  const handleDelete = async (path) => {
    if (!window.confirm('Bu öğeyi silmek istediğinizden emin misiniz?')) {
      return;
    }
  
    try {
      const updatedData = { ...portfolioData };
      const pathParts = path.split('.');
      const category = pathParts[0]; // CASH
  
      // Para birimi silme kontrolü
      if (path.endsWith('CURRENCY')) {
        // assetKey oluştur (örn: "EUR.CURRENCY" veya "USD.CURRENCY")
        const assetKey = pathParts.slice(-2).join('.');
        
        console.log('Deleting currency:', {
          category,
          assetKey,
          assets: updatedData[category]?.assets
        });
  
        if (updatedData[category]?.assets && updatedData[category].assets[assetKey]) {
          delete updatedData[category].assets[assetKey];
          await updateCategory(userEmail, category, updatedData[category], selectedPortfolioId);
          console.log('Currency deleted successfully:', assetKey);
        }
      } else {
        // Normal kategori/asset silme işlemi
        if (pathParts.length === 1) {
          await deleteCategory(userEmail, category, selectedPortfolioId);
          delete updatedData[category];
        } else {
          let current = updatedData[category];
          for (let i = 1; i < pathParts.length - 1; i++) {
            current = current[pathParts[i]];
          }
          delete current[pathParts[pathParts.length - 1]];
          await updateCategory(userEmail, category, updatedData[category], selectedPortfolioId);
        }
      }
      
      setPortfolioData(updatedData);
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };




  const handleAllocationChange = async (path, newValue) => {
    try {
      const updatedData = { ...portfolioData };
      const parts = path.split('.');
      const category = parts[0];
  
      let current = updatedData;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) current[parts[i]] = {};
        current = current[parts[i]];
      }
      
      if (!current[parts[parts.length - 1]]) {
        current[parts[parts.length - 1]] = {};
      }
      current[parts[parts.length - 1]].allocation = parseFloat(newValue);
  
      await updateCategory(userEmail, category, updatedData[category], selectedPortfolioId);
      setPortfolioData(updatedData);
    } catch (error) {
      console.error('Error updating allocation:', error);
    }
  };

  return {
    portfolioData,
    
    loading,
    modalState,
    handleAddAsset,
    handleAssetSubmit,
    handleAddCategory,
    handleCategorySubmit,
    handleDelete,
    handleAllocationChange,
    setModalState
  };
}